import { get } from 'lodash';
import { questionnaireQuestionTypes, defaultQuestionType, questionsTypesToShowOptions } from '../../Components/Questionnaires/QuestionnaireConstants';
import {SignDocTypes} from '../../Components/SignDoc/SignDocLib';

/**
 * Helper functions and constants for Questionnaire contexts
 */

/**
 * Get default values and constants based on i18n context
 * @param {object} i18n - i18n context object
 * @returns {object} - Object containing default values and constants
 */
export const getQuestionnaireDefaults = ({ i18n }) => {
    const defaultQuestionText = i18n.translate('questionnaireQuestions.placeHolders.question');
    const defaultOptionText = i18n.translate('questionnaireQuestions.placeHolders.option');
    const ratingScaleMinValue = 1;
    const ratingScaleMaxValue = 5;
    const ratingMinLabel = i18n.translate('questionnaireQuestions.survey.ratingScale.defaultMinLabel');
    const ratingMaxLabel = i18n.translate('questionnaireQuestions.survey.ratingScale.defaultMaxLabel');
    
    const defaultRatingScaleConfig = {
        minValue: ratingScaleMinValue,
        maxValue: ratingScaleMaxValue,
        minLabel: ratingMinLabel,
        maxLabel: ratingMaxLabel
    };

    return {
        defaultQuestionText,
        defaultOptionText,
        ratingScaleMinValue,
        ratingScaleMaxValue,
        ratingMinLabel,
        ratingMaxLabel,
        defaultRatingScaleConfig
    };
};

/**
 * Create a new question object
 * @param {object} params - Parameters object
 * @param {number} params.position - Position of the question
 * @param {string} params.defaultQuestionText - Default question text
 * @param {function} params.createOptionFn - Function to create option
 * @returns {object} - New question object
 */
export const createQuestion = ({ position = 0, defaultQuestionText, createOptionFn }) => {
    return {
        text: defaultQuestionText,
        options: [createOptionFn({ position: 1 })],
        type: defaultQuestionType,
        position,
        points: null,
        isRandomiseAnswersOrder: false,
    };
};

/**
 * Create a new option object
 * @param {object} params - Parameters object
 * @param {number} params.position - Position of the option
 * @param {string} params.defaultOptionText - Default option text
 * @returns {object} - New option object
 */
export const createOption = ({ position, defaultOptionText }) => {
    return {
        text: `${defaultOptionText} ${position}`,
        isCorrect: false,
    };
};

/**
 * Validate questionnaire questions
 * @param {object} params - Parameters object
 * @param {Array} params.questionnaireQuestions - Array of questionnaire questions
 * @param {object} params.i18n - i18n context object
 * @param {string} params.questionnaireType - Type of questionnaire
 * @returns {object} - Validation result with isValid flag and errors array
 */
export const validateQuestionnaireQuestions = ({ questionnaireQuestions, i18n, questionnaireType }) => {
    const errors = [];

    questionnaireQuestions.forEach((question, index) => {
        const questionErrors = [];
        const options = question.options || [];

        // Check if the question has at least 2 options for questions that need options
        if (questionsTypesToShowOptions.includes(question.type) && options.length < 2) {
            questionErrors.push({
                errorType: 'MINIMUM_OPTIONS',
                errorMessage: i18n.translate('questionnaireQuestions.validation.minimumOptions')
            });
        }

        // Check if the question has at least 1 correct answer for questions that need options
        if (questionsTypesToShowOptions.includes(question.type) && questionnaireType !== SignDocTypes.SURVEY) {
            const hasCorrectAnswer = options.some(option => option.isCorrect);
            if (!hasCorrectAnswer) {
                questionErrors.push({
                    errorType: 'NO_CORRECT_ANSWER',
                    errorMessage: i18n.translate('questionnaireQuestions.validation.noCorrectAnswer')
                });
            }
        }

        // Check for duplicate option text
        if (questionsTypesToShowOptions.includes(question.type)) {
            const optionTexts = {};
            const duplicateOptions = [];

            options.forEach(option => {
                const trimmedText = option.text.trim();
                if (optionTexts[trimmedText]) {
                    duplicateOptions.push({
                        id: option.id,
                        text: trimmedText
                    });
                } else {
                    optionTexts[trimmedText] = true;
                }

                // Check for empty option text
                if (!trimmedText) {
                    questionErrors.push({
                        errorType: 'EMPTY_OPTION_VALUE',
                        errorMessage: i18n.translate('questionnaireQuestions.validation.emptyOptionValue')
                    });
                }
            });

            if (duplicateOptions.length > 0) {
                questionErrors.push({
                    errorType: 'DUPLICATE_OPTIONS',
                    errorMessage: i18n.translate('questionnaireQuestions.validation.duplicateOptions'),
                    duplicateOptions
                });
            }
        }

        if (questionErrors.length > 0) {
            errors.push({
                questionId: question.id,
                questionPosition: index,
                errors: questionErrors
            });
        }
    });

    return {
        isValid: errors.length === 0,
        errors
    };
};